import { html, attr, Value } from '@tempots/dom'
import { Icon } from '@tempots/beatui'
import type { TNode } from '@tempots/dom'

export type BadgeVariant =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral'
  | 'purple'

export interface StatusBadgeOptions {
  label: Value<string>
  variant: Value<BadgeVariant>
  icon?: Value<string>
}

/**
 * Unified status badge component
 *
 * @example
 * StatusBadge({
 *   label: status.map(s => s.charAt(0).toUpperCase() + s.slice(1)),
 *   variant: status.map(mapStatusToVariant),
 *   icon: status.map(s => statusIcons[s])
 * })
 */
export function StatusBadge(options: StatusBadgeOptions): TNode {
  const { label, variant, icon } = options

  const variantStyles: Record<BadgeVariant, string> = {
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800',
    neutral: 'bg-gray-100 text-gray-800',
    purple: 'bg-purple-100 text-purple-800',
  }

  return html.span(
    attr.class(
      Value.map(
        variant,
        (v) =>
          `inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
            variantStyles[v] ?? variantStyles.neutral
          }`
      )
    ),
    icon
      ? Icon({ icon: Value.toSignal(icon) }, attr.class('w-3.5 h-3.5'))
      : null,
    label
  )
}
